import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const FuelCost = () => {
  const [distance, setDistance] = useState("");
  const [fuelEfficiency, setFuelEfficiency] = useState("");
  const [fuelPrice, setFuelPrice] = useState("");
  const [fuelCost, setFuelCost] = useState("0.00");

  // useEffect hook for recalculating fuel cost whenever inputs change
  useEffect(() => {
    if (distance && fuelEfficiency && fuelPrice) {
      const cost =
        (parseFloat(distance) / parseFloat(fuelEfficiency)) *
        parseFloat(fuelPrice);
      setFuelCost(cost.toFixed(2));
    } else {
      setFuelCost("0.00");
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [distance, fuelEfficiency, fuelPrice]);

  // Handlers for input changes
  const handleDistanceChange = (e) => {
    setDistance(e.target.value);
  };

  const handleFuelEfficiencyChange = (e) => {
    setFuelEfficiency(e.target.value);
  };

  const handleFuelPriceChange = (e) => {
    setFuelPrice(e.target.value);
  };

  return (
    <Layout title="Fuel Cost Calculator" path="fuel-cost">
      <section
        className="fuel-cost-calculator-section section-padding"
        id="fuel-cost-calculator"
      >
        <div className="container mt-5">
          <div className="row">
            {/* Fuel Cost Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Fuel Cost Calculator</h3>
                <div className="container">
                  {/* Input Fields for Fuel Cost Calculation */}
                  <div className="row gy-3">
                    {/* Distance */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="distance" className="form-label">
                        Distance (km)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Distance:</strong> The total distance you plan to travel."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="distance"
                        placeholder="Enter distance"
                        value={distance}
                        onChange={handleDistanceChange}
                      />
                      {/* <small className="form-text text-muted">Enter the distance in kilometers</small> */}
                    </div>

                    {/* Fuel Efficiency */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="fuelEfficiency" className="form-label">
                        Fuel Efficiency (km/l)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Fuel Efficiency:</strong> The vehicle's mileage in kilometers per liter."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="fuelEfficiency"
                        placeholder="Enter fuel efficiency"
                        value={fuelEfficiency}
                        onChange={handleFuelEfficiencyChange}
                      />
                      {/* <small className="form-text text-muted">Enter the vehicle's fuel efficiency</small> */}
                    </div>

                    {/* Fuel Price */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="fuelPrice" className="form-label">
                        Fuel Price (₹/l)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Fuel Price:</strong> The cost of fuel per liter in Rupees."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="fuelPrice"
                        placeholder="Enter fuel price"
                        value={fuelPrice}
                        onChange={handleFuelPriceChange}
                      />
                      {/* <small className="form-text text-muted">Enter the fuel price per liter in Rupees</small> */}
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Estimated Fuel Cost:{" "}
                        <span className="fw-bold ms-3">₹{fuelCost}</span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Fuel Cost Calculator
                </h3>

                {/* What is a Fuel Cost Calculator? */}
                <h4 className="mt-3 mb-3">What is a Fuel Cost Calculator?</h4>
                <p>
                  A **Fuel Cost Calculator** is a tool designed to help
                  individuals and businesses estimate the total cost of fuel for
                  a specific journey based on distance, vehicle fuel efficiency,
                  and the current fuel price.
                </p>

                {/* Why Use a Fuel Cost Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Fuel Cost Calculator?</h4>
                <p>
                  Using a fuel cost calculator provides numerous advantages:
                </p>
                <ul>
                  <li>
                    <strong>Cost Estimation:</strong> Accurately estimates
                    travel expenses.
                  </li>
                  <li>
                    <strong>Budgeting:</strong> Aids in financial planning for
                    personal or business trips.
                  </li>
                  <li>
                    <strong>Efficiency Planning:</strong> Promotes
                    fuel-efficient travel by highlighting costs.
                  </li>
                  <li>
                    <strong>Business Utility:</strong> Beneficial for logistics
                    and transportation management.
                  </li>
                  <li>
                    <strong>Comparison:</strong> Helps compare costs for
                    different vehicles or routes.
                  </li>
                </ul>

                {/* How to Use a Fuel Cost Calculator? */}
                <h4 className="mt-4 mb-3">
                  How to Use a Fuel Cost Calculator?
                </h4>
                <p>To use this calculator, follow these steps:</p>
                <ul>
                  <li>Input the **Distance** to be traveled in kilometers.</li>
                  <li>
                    Enter the **Fuel Efficiency** of your vehicle in km/l.
                  </li>
                  <li>Provide the **Fuel Price** per liter in Rupees.</li>
                  <li>
                    The calculator will instantly display the estimated fuel
                    cost.
                  </li>
                </ul>

                {/* Common Factors Affecting Fuel Cost Calculation */}
                <h4 className="mt-4 mb-3">
                  Common Factors Affecting Fuel Cost Calculation
                </h4>
                <p>The main factors influencing fuel cost include:</p>
                <ul>
                  <li>
                    <strong>Distance:</strong> Longer trips result in higher
                    fuel costs.
                  </li>
                  <li>
                    <strong>Fuel Efficiency:</strong> Higher fuel efficiency
                    lowers overall cost.
                  </li>
                  <li>
                    <strong>Fuel Price:</strong> Fluctuating fuel prices impact
                    total expenses.
                  </li>
                </ul>

                {/* Benefits of Using a Fuel Cost Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a Fuel Cost Calculator
                </h4>
                <p>Key benefits include:</p>
                <ul>
                  <li>
                    <strong>Accurate Cost Planning:</strong> Avoid unexpected
                    expenses.
                  </li>
                  <li>
                    <strong>Better Financial Management:</strong> Helps set
                    budgets for trips.
                  </li>
                  <li>
                    <strong>Trip Optimization:</strong> Allows for route and
                    vehicle choice adjustments.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default FuelCost;
